// app/(app)/invoices/[id].tsx
import { Button, Input } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { invoiceApi } from "@/services/invoiceApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearError,
  confirmInvoice,
  fetchInvoiceById,
  recordPayment,
  resetSelected,
  revertToDraft,
} from "@/store/slices/invoiceSlice";
import {
  approveReturn,
  bulkApproveReturns,
  createReturnRequest,
  rejectReturn,
} from "@/store/slices/returnSlice";
import { showAlert } from "@/utils/alert";
import * as FileSystem from "expo-file-system/legacy";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

// Helper functions
const isWithinOneHour = (createdAt?: string): boolean => {
  if (!createdAt) return false;
  const createdTime = new Date(createdAt).getTime();
  const now = new Date().getTime();
  const diffMs = now - createdTime;
  return diffMs <= 60 * 60 * 1000;
};

const getRemainingQuantity = (
  invoiceLines: any[],
  returnRequests: any[],
  productId: number,
): number => {
  const line = invoiceLines.find((l) => l.productId === productId);
  if (!line) return 0;
  const originalSold = line.quantity;
  const approvedTotal = returnRequests
    .filter((r) => r.productId === productId && r.status === "approved")
    .reduce((sum, r) => sum + r.quantity, 0);
  return originalSold - approvedTotal;
};

const hasPendingReturn = (
  returnRequests: any[],
  productId: number,
): boolean => {
  return returnRequests.some(
    (r) => r.productId === productId && r.status === "pending",
  );
};

export default function InvoiceDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { selectedInvoice, loading, error, updating } = useAppSelector(
    (state) => state.invoices,
  );
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = user?.userType === "Owner";

  // Payment modal
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState<
    "cash" | "bank" | "upi" | "cheque"
  >("cash");
  const [paymentRef, setPaymentRef] = useState("");

  // Return modal (multi-product / full return)
  const [returnModalVisible, setReturnModalVisible] = useState(false);
  const [fullInvoiceReturn, setFullInvoiceReturn] = useState(false);
  const [commonReason, setCommonReason] = useState("");
  const [returnItems, setReturnItems] = useState<
    { productId: number; quantity: number; reason: string }[]
  >([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [returnQty, setReturnQty] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [returnErrors, setReturnErrors] = useState<{ [key: string]: string }>(
    {},
  );

  // Bulk approve state
  const [selectedReturnIds, setSelectedReturnIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Reject modal
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReturnId, setRejectReturnId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const router = useRouter();

  const handleEdit = () => {
    router.push(`/invoices/edit/${id}`);
  };

  const handleRevertToDraft = async () => {
    showAlert(
      "Revert to Draft",
      "This will move the invoice back to draft. Only allowed if created within the last 1 hour and no payments recorded. Continue?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Confirm",
          onPress: async () => {
            const result = await dispatch(revertToDraft(Number(id)));
            if (revertToDraft.fulfilled.match(result)) {
              showAlert("Success", "Invoice reverted to draft");
              dispatch(fetchInvoiceById(Number(id)));
            }
          },
        },
      ],
    );
  };

  useFocusEffect(
    useCallback(() => {
      if (id) {
        dispatch(fetchInvoiceById(Number(id)));
      }
    }, [id, dispatch]),
  );

  useEffect(() => {
    return () => {
      dispatch(resetSelected());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedInvoice) {
      console.log("Invoice detail:", JSON.stringify(selectedInvoice, null, 2));
    }
  }, [selectedInvoice]);

  useEffect(() => {
    if (error) {
      showAlert("Error", error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleConfirm = () => {
    showAlert(
      "Confirm Invoice",
      "This will deduct stock and increase customer balance. Continue?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Confirm",
          onPress: async () => {
            const result = await dispatch(confirmInvoice(Number(id)));
            if (confirmInvoice.fulfilled.match(result)) {
              showAlert("Confirmed", "Invoice confirmed");
              dispatch(fetchInvoiceById(Number(id)));
            }
          },
        },
      ],
    );
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      showAlert("Error", "Enter a valid amount");
      return;
    }
    const result = await dispatch(
      recordPayment({
        invoiceId: Number(id),
        amount,
        paymentDate: new Date().toISOString().split("T")[0],
        paymentMode,
        referenceNo: paymentRef || undefined,
      }),
    );
    if (recordPayment.fulfilled.match(result)) {
      showAlert("Payment Recorded", "Payment added");
      setPaymentModalVisible(false);
      dispatch(fetchInvoiceById(Number(id)));
    }
  };

  // ---------- Return Handlers ----------
  const handleAddReturnItem = () => {
    if (!selectedInvoice) return;
    if (!selectedProductId) {
      setReturnErrors((prev) => ({ ...prev, product: "Select a product" }));
      return;
    }
    const qty = parseInt(returnQty);
    if (!qty || qty <= 0) {
      setReturnErrors((prev) => ({ ...prev, qty: "Enter valid quantity" }));
      return;
    }
    const product = selectedInvoice.InvoiceLines?.find(
      (i) => i.productId === parseInt(selectedProductId),
    );
    if (!product) return;
    const remaining = getRemainingQuantity(
      selectedInvoice.InvoiceLines ?? [],
      selectedInvoice.ReturnRequests ?? [],
      product.productId,
    );
    if (qty > remaining) {
      setReturnErrors((prev) => ({ ...prev, qty: `Max ${remaining} units` }));
      return;
    }
    if (
      hasPendingReturn(selectedInvoice.ReturnRequests ?? [], product.productId)
    ) {
      setReturnErrors((prev) => ({
        ...prev,
        product: "Pending request exists",
      }));
      return;
    }
    if (returnItems.some((i) => i.productId === product.productId)) {
      setReturnErrors((prev) => ({
        ...prev,
        product: "Product already added",
      }));
      return;
    }
    setReturnItems((prev) => [
      ...prev,
      {
        productId: product.productId,
        quantity: qty,
        reason: returnReason.trim() || commonReason,
      },
    ]);
    setSelectedProductId("");
    setReturnQty("");
    setReturnReason("");
    setReturnErrors({});
  };

  const handleRemoveReturnItem = (productId: number) => {
    setReturnItems((prev) => prev.filter((i) => i.productId !== productId));
  };

  const handleSubmitReturn = async () => {
    if (fullInvoiceReturn) {
      const result = await dispatch(
        createReturnRequest({
          invoiceId: Number(id),
          fullInvoiceReturn: true,
          reason: commonReason.trim() || undefined,
        }),
      );
      if (createReturnRequest.fulfilled.match(result)) {
        const response = result.payload;
        showAlert(
          "Success",
          response.message || "Full return request submitted",
        );
        setReturnModalVisible(false);
        setReturnItems([]);
        setCommonReason("");
        setFullInvoiceReturn(false);
        dispatch(fetchInvoiceById(Number(id)));
      }
      return;
    }

    if (returnItems.length === 0) {
      showAlert("Error", "Add at least one product");
      return;
    }
    const payload: any = {
      invoiceId: Number(id),
      items: returnItems.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        reason: item.reason || commonReason.trim() || undefined,
      })),
    };
    if (commonReason.trim()) payload.reason = commonReason.trim();
    const result = await dispatch(createReturnRequest(payload));
    if (createReturnRequest.fulfilled.match(result)) {
      const response = result.payload;
      if (response.errors && response.errors.length > 0) {
        showAlert(
          "Partial Success",
          response.message + "\nErrors:\n" + response.errors.join("\n"),
        );
      } else {
        showAlert("Success", response.message || "Return requests submitted");
      }
      setReturnModalVisible(false);
      setReturnItems([]);
      setCommonReason("");
      setFullInvoiceReturn(false);
      dispatch(fetchInvoiceById(Number(id)));
    }
  };

  // ---------- Approve / Reject ----------
  const handleApproveReturn = (returnReqId: number) => {
    showAlert(
      "Approve Return",
      "This will add stock and decrease customer balance. Continue?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Approve",
          onPress: async () => {
            const result = await dispatch(approveReturn(returnReqId));
            if (approveReturn.fulfilled.match(result)) {
              showAlert("Approved", "Return approved");
              dispatch(fetchInvoiceById(Number(id)));
            }
          },
        },
      ],
    );
  };

  const handleRejectReturn = (returnReqId: number) => {
    setRejectReturnId(returnReqId);
    setRejectionReason("");
    setRejectModalVisible(true);
  };

  const confirmReject = async () => {
    if (!rejectReturnId) return;
    const result = await dispatch(
      rejectReturn({
        id: rejectReturnId,
        rejectionReason: rejectionReason.trim() || undefined,
      }),
    );
    if (rejectReturn.fulfilled.match(result)) {
      showAlert("Rejected", "Return request rejected");
      setRejectModalVisible(false);
      setRejectReturnId(null);
      setRejectionReason("");
      dispatch(fetchInvoiceById(Number(id)));
    }
  };

  // ---------- Bulk Approve ----------
  const handleBulkApprove = async (approveAll: boolean) => {
    const payload: any = { invoiceId: Number(id) };
    if (approveAll) {
      payload.approveAll = true;
    } else {
      if (selectedReturnIds.length === 0) {
        showAlert("Error", "No returns selected");
        return;
      }
      payload.returnRequestIds = selectedReturnIds;
    }
    const result = await dispatch(bulkApproveReturns(payload));
    if (bulkApproveReturns.fulfilled.match(result)) {
      showAlert("Success", result.payload.message || "Returns approved");
      setSelectedReturnIds([]);
      setSelectAll(false);
      dispatch(fetchInvoiceById(Number(id)));
    }
  };

  const toggleSelectAll = () => {
    const pendingReturns =
      selectedInvoice?.ReturnRequests?.filter((r) => r.status === "pending") ||
      [];
    if (selectAll) {
      setSelectedReturnIds([]);
    } else {
      setSelectedReturnIds(pendingReturns.map((r) => r.id));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelectReturn = (id: number) => {
    setSelectedReturnIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handlePrintShare = async () => {
    try {
      const blob = await invoiceApi.printInvoice(Number(id));
      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri = FileSystem.cacheDirectory + `invoice_${id}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (err) {
      console.error(err);
      showAlert("Error", "Could not generate PDF");
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: colors.background,
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    infoRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.sm,
    },
    label: { fontSize: 14, color: colors.textLight },
    value: { fontSize: 14, color: colors.text, fontWeight: "500" },
    itemCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.sm,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    returnCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.sm,
      marginBottom: spacing.sm,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
      borderWidth: 1,
      borderColor: colors.border,
    },
    returnStatus: {
      fontSize: 12,
      fontWeight: "500",
    },
    buttonRow: {
      marginTop: spacing.md,
      gap: spacing.sm,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.5)",
      justifyContent: "center",
      alignItems: "center",
      padding: spacing.lg,
    },
    modalContent: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
      width: "100%",
      maxWidth: 500,
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    disabledMessage: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.sm,
    },
    returnActionRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
    // Return modal styles
    switchRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: spacing.md,
    },
    switchLabel: { fontSize: 16, color: colors.text },
    returnAddSection: { marginBottom: spacing.md },
    returnAddRow: { flexDirection: "row", alignItems: "center" },
    returnItemsList: { marginBottom: spacing.md },
    returnItemCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.sm,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    returnItemRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 2,
    },
    returnItemName: { fontSize: 14, fontWeight: "500", color: colors.text },
    returnItemDetail: { fontSize: 12, color: colors.textLight },
    returnRemoveBtn: { padding: 4 },
    // Bulk approve styles
    bulkActionsRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.sm,
      marginVertical: spacing.sm,
    },
    returnCheckboxRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.xs,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: colors.border,
      marginRight: spacing.sm,
      justifyContent: "center",
      alignItems: "center",
    },
    checkboxChecked: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    checkboxLabel: { fontSize: 14, color: colors.text, flex: 1 },
    selectAllRow: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.sm,
    },
    selectAllText: { fontSize: 14, fontWeight: "500", color: colors.text },
  });

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }
  if (!selectedInvoice) return null;

  const invoiceNo = selectedInvoice.invoiceNo ?? "N/A";
  const customer = selectedInvoice.Customer ?? {
    fullName: "Unknown",
    phone: "",
  };
  const invoiceDate = selectedInvoice.invoiceDate
    ? new Date(selectedInvoice.invoiceDate).toLocaleDateString()
    : "No date";
  const status = selectedInvoice.status ?? "draft";
  const isDraft = status === "draft";
  const items = selectedInvoice.InvoiceLines ?? [];
  const returns = selectedInvoice.ReturnRequests ?? [];
  const pendingReturns = returns.filter((r) => r.status === "pending");

  const totalAmount = parseFloat(selectedInvoice.totalAmount ?? "0");
  const taxAmount = parseFloat(selectedInvoice.taxAmount ?? "0");
  const discount = parseFloat(selectedInvoice.discount ?? "0");
  const grandTotal = parseFloat(selectedInvoice.grandTotal ?? "0");

  const withinOneHour = isWithinOneHour(selectedInvoice.createdAt);

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.title}>{invoiceNo}</Text>

          <View style={styles.infoRow}>
            <Text style={styles.label}>Customer:</Text>
            <Text style={styles.value}>
              {customer.fullName} ({customer.phone})
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>{invoiceDate}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Status:</Text>
            <Text
              style={[styles.value, { color: isDraft ? "#f59e0b" : "#22c55e" }]}
            >
              {status.toUpperCase()}
            </Text>
          </View>

          <Text style={styles.sectionTitle}>Items</Text>
          {items.length === 0 ? (
            <Text style={{ color: colors.textLight }}>No items found</Text>
          ) : (
            items.map((item) => (
              <View key={item.id} style={styles.itemCard}>
                <Text style={{ fontWeight: "500", color: colors.text }}>
                  {item.Product?.name || `Product ${item.productId}`}
                </Text>
                <Text
                  style={{
                    color: colors.textLight,
                    fontSize: 12,
                    marginTop: 2,
                  }}
                >
                  {item.quantity} × ₹{parseFloat(item.unitPrice).toFixed(2)} = ₹
                  {parseFloat(item.lineTotal).toFixed(2)}
                </Text>
              </View>
            ))
          )}

          <View style={styles.infoRow}>
            <Text style={styles.label}>Subtotal:</Text>
            <Text style={styles.value}>₹{totalAmount.toFixed(2)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Tax:</Text>
            <Text style={styles.value}>₹{taxAmount.toFixed(2)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Discount:</Text>
            <Text style={styles.value}>₹{discount.toFixed(2)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Grand Total:</Text>
            <Text
              style={[
                styles.value,
                { fontWeight: "700", color: colors.primary },
              ]}
            >
              ₹{grandTotal.toFixed(2)}
            </Text>
          </View>

          {/* Return Requests Section */}
          {returns.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>Return Requests</Text>
              {returns.map((r) => {
                const statusColor =
                  r.status === "approved"
                    ? colors.success
                    : r.status === "pending"
                      ? colors.disabled
                      : colors.danger;
                return (
                  <View key={r.id} style={styles.returnCard}>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text style={{ fontWeight: "600", color: colors.text }}>
                        Return #{r.id}
                      </Text>
                      <Text
                        style={[styles.returnStatus, { color: statusColor }]}
                      >
                        {r.status.toUpperCase()}
                      </Text>
                    </View>
                    <Text style={{ color: colors.text }}>
                      Product: {r.Product.name} – Qty: {r.quantity}
                    </Text>
                    <Text style={{ color: colors.text }}>
                      Reason: {r.reason}
                    </Text>
                    {/* Individual Approve/Reject (Owner only) */}
                    {r.status === "pending" && (
                      <View style={styles.returnActionRow}>
                        <Button
                          title="Approve"
                          onPress={() => handleApproveReturn(r.id)}
                          variant="primary"
                          containerStyle={{ flex: 1 }}
                        />
                        <Button
                          title="Reject"
                          onPress={() => handleRejectReturn(r.id)}
                          variant="danger"
                          containerStyle={{ flex: 1 }}
                        />
                      </View>
                    )}
                  </View>
                );
              })}

              {/* Bulk Approve (Owner only) */}
              {pendingReturns.length > 0 && (
                <>
                  {/* Select All */}
                  <View style={styles.selectAllRow}>
                    <TouchableOpacity
                      style={[
                        styles.checkbox,
                        selectAll && styles.checkboxChecked,
                      ]}
                      onPress={toggleSelectAll}
                    >
                      {selectAll && <Text style={{ color: "#fff" }}>✓</Text>}
                    </TouchableOpacity>
                    <Text style={styles.selectAllText}>Select All</Text>
                  </View>

                  {/* Pending returns with checkboxes */}
                  {pendingReturns.map((r) => (
                    <View key={r.id} style={styles.returnCheckboxRow}>
                      <TouchableOpacity
                        style={[
                          styles.checkbox,
                          selectedReturnIds.includes(r.id) &&
                            styles.checkboxChecked,
                        ]}
                        onPress={() => toggleSelectReturn(r.id)}
                      >
                        {selectedReturnIds.includes(r.id) && (
                          <Text style={{ color: "#fff" }}>✓</Text>
                        )}
                      </TouchableOpacity>
                      <Text style={styles.checkboxLabel}>
                        Return #{r.id} – Qty: {r.quantity}
                      </Text>
                    </View>
                  ))}

                  <View style={styles.bulkActionsRow}>
                    <Button
                      title="Approve Selected"
                      onPress={() => handleBulkApprove(false)}
                      variant="primary"
                      disabled={selectedReturnIds.length === 0}
                    />
                    <Button
                      title="Approve All"
                      onPress={() => handleBulkApprove(true)}
                      // variant="success"
                    />
                  </View>
                </>
              )}
            </>
          )}

          <View style={styles.buttonRow}>
            {isDraft &&
              (withinOneHour ? (
                <>
                  <Button
                    title="Edit Invoice"
                    onPress={handleEdit}
                    variant="outline"
                  />
                  <Button
                    title="Confirm Invoice"
                    onPress={handleConfirm}
                    loading={updating}
                  />
                </>
              ) : (
                <Text style={styles.disabledMessage}>
                  Actions disabled (older than 1 hour)
                </Text>
              ))}
            {!isDraft && withinOneHour && (
              <Button
                title="Revert to Draft"
                onPress={handleRevertToDraft}
                variant="outline"
              />
            )}
            <Button
              title="Record Payment"
              onPress={() => setPaymentModalVisible(true)}
              variant="outline"
              disabled
            />
            <Button
              title="Request Return"
              onPress={() => setReturnModalVisible(true)}
              variant="outline"
            />
            <Button
              title="Print/Share PDF"
              onPress={handlePrintShare}
              variant="outline"
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Payment Modal */}
      <Modal visible={paymentModalVisible} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setPaymentModalVisible(false)}
          />
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Record Payment</Text>
            <Input
              label="Amount (₹)"
              value={paymentAmount}
              onChangeText={setPaymentAmount}
              keyboardType="numeric"
            />
            <SearchableSelect
              label="Payment Mode"
              options={[
                { label: "Cash", value: "cash" },
                { label: "Bank Transfer", value: "bank" },
                { label: "UPI", value: "upi" },
                { label: "Cheque", value: "cheque" },
              ]}
              value={paymentMode}
              onValueChange={(val) => setPaymentMode(val as any)}
            />
            <Input
              label="Reference No (optional)"
              value={paymentRef}
              onChangeText={setPaymentRef}
            />
            <Button
              title="Submit Payment"
              onPress={handleRecordPayment}
              loading={updating}
            />
            <Button
              title="Cancel"
              variant="outline"
              onPress={() => setPaymentModalVisible(false)}
              containerStyle={{ marginTop: spacing.sm }}
            />
          </View>
        </View>
      </Modal>

      {/* Return Request Modal */}
      <Modal visible={returnModalVisible} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setReturnModalVisible(false)}
          />
          <ScrollView contentContainerStyle={styles.modalContent}>
            <Text style={styles.modalTitle}>Request Return</Text>

            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Full Invoice Return</Text>
              <Switch
                value={fullInvoiceReturn}
                onValueChange={(val) => {
                  setFullInvoiceReturn(val);
                  if (val) {
                    setReturnItems([]);
                    setSelectedProductId("");
                    setReturnQty("");
                    setReturnReason("");
                  }
                }}
                trackColor={{ false: colors.border, true: colors.primary }}
                thumbColor={colors.background}
              />
            </View>

            {!fullInvoiceReturn && (
              <>
                <View style={styles.returnAddSection}>
                  <SearchableSelect
                    label="Product"
                    placeholder="Select product"
                    options={items
                      .filter(
                        (item) =>
                          getRemainingQuantity(items, returns, item.productId) >
                            0 &&
                          !hasPendingReturn(returns, item.productId) &&
                          !returnItems.some(
                            (i) => i.productId === item.productId,
                          ),
                      )
                      .map((item) => {
                        const remaining = getRemainingQuantity(
                          items,
                          returns,
                          item.productId,
                        );
                        return {
                          label: `${item.Product?.name || `Product ${item.productId}`} (Remaining: ${remaining})`,
                          value: item.productId.toString(),
                        };
                      })}
                    value={selectedProductId}
                    onValueChange={setSelectedProductId}
                    containerStyle={{ marginBottom: spacing.sm }}
                    error={returnErrors.product}
                  />
                  <View style={styles.returnAddRow}>
                    <Input
                      label="Quantity"
                      value={returnQty}
                      onChangeText={setReturnQty}
                      keyboardType="numeric"
                      containerStyle={{ flex: 1, marginRight: spacing.sm }}
                      error={returnErrors.qty}
                    />
                    <Button
                      title="Add"
                      onPress={handleAddReturnItem}
                      variant="primary"
                      // size="sm"
                      containerStyle={{
                        alignSelf: "flex-end",
                        marginBottom: spacing.sm,
                      }}
                    />
                  </View>
                  <Input
                    label="Reason (per item)"
                    value={returnReason}
                    onChangeText={setReturnReason}
                    placeholder="Optional reason for this product"
                    containerStyle={{ marginBottom: spacing.sm }}
                  />
                </View>

                {returnItems.length > 0 && (
                  <View style={styles.returnItemsList}>
                    <Text style={styles.sectionTitle}>Products to Return</Text>
                    {returnItems.map((item, idx) => {
                      const product = items.find(
                        (i) => i.productId === item.productId,
                      );
                      return (
                        <View key={idx} style={styles.returnItemCard}>
                          <View style={styles.returnItemRow}>
                            <Text style={styles.returnItemName}>
                              {product?.Product?.name ||
                                `Product ${item.productId}`}
                            </Text>
                            <TouchableOpacity
                              onPress={() =>
                                handleRemoveReturnItem(item.productId)
                              }
                              style={styles.returnRemoveBtn}
                            >
                              <Text
                                style={{ color: colors.danger, fontSize: 18 }}
                              >
                                ✕
                              </Text>
                            </TouchableOpacity>
                          </View>
                          <Text style={styles.returnItemDetail}>
                            Qty: {item.quantity}
                          </Text>
                          {item.reason && (
                            <Text style={styles.returnItemDetail}>
                              Reason: {item.reason}
                            </Text>
                          )}
                        </View>
                      );
                    })}
                  </View>
                )}
              </>
            )}

            <Input
              label={
                fullInvoiceReturn ? "Return Reason" : "Common Reason (optional)"
              }
              value={commonReason}
              onChangeText={setCommonReason}
              placeholder={
                fullInvoiceReturn
                  ? "Reason for full return"
                  : "Optional common reason"
              }
              containerStyle={{ marginBottom: spacing.md }}
            />

            <Button
              title="Submit Return Request"
              onPress={handleSubmitReturn}
              loading={updating}
              disabled={!fullInvoiceReturn && returnItems.length === 0}
            />
            <Button
              title="Cancel"
              variant="outline"
              onPress={() => {
                setReturnModalVisible(false);
                setReturnItems([]);
                setCommonReason("");
                setFullInvoiceReturn(false);
                setReturnErrors({});
              }}
              containerStyle={{ marginTop: spacing.sm }}
            />
          </ScrollView>
        </View>
      </Modal>

      {/* Reject Reason Modal */}
      <Modal visible={rejectModalVisible} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setRejectModalVisible(false)}
          />
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Reject Return Request</Text>
            <Input
              label="Rejection Reason (optional)"
              value={rejectionReason}
              onChangeText={setRejectionReason}
              placeholder="Reason for rejection (optional)"
              containerStyle={{ marginBottom: spacing.md }}
            />
            <Button
              title="Confirm Reject"
              onPress={confirmReject}
              loading={updating}
              variant="danger"
            />
            <Button
              title="Cancel"
              variant="outline"
              onPress={() => {
                setRejectModalVisible(false);
                setRejectReturnId(null);
                setRejectionReason("");
              }}
              containerStyle={{ marginTop: spacing.sm }}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
}
